import { Box } from "@chakra-ui/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import NuSingleSelect from "./NuSingleSelect";
import NuSelectItem from "./NuSelectItem";

export default class NuMultiSelect extends NuSingleSelect {
	constructor(props) {
		super(props);
		this.state = {
			selectedItems: [], //this.findSelectedItems(this.props.selectedItems),
			filteredItems: [],
			filter: "",
			openSelection: false,
			cbLoading: false,
		};
	}

	findSelectedItems = (selectedValues) => {
		const selectedItems = [];
		for (const value of selectedValues) {
			for (const item of this.props.allItems) {
				if (item["value"] === value) {
					selectedItems.push(item);
					break;
				}
			}
			/* We have failed to match with an existing item */
			if (selectedItems.length === 0 || selectedItems[selectedItems.length - 1]["value"] !== value) {
				selectedItems.push({
					text: value,
					value: value,
				});
			}
		}
		return selectedItems;
	};

	componentDidMount() {
		document.addEventListener("mousedown", this.handleClickOutside, { capture: true });
		window.addEventListener("blur", this.handleWindowBlur);
		this.setState({
			selectedItems: this.findSelectedItems(this.props.selectedItems),
		});
	}

	componentWillUnmount() {
		window.removeEventListener("blur", this.handleWindowBlur);
		document.removeEventListener("mousedown", this.handleClickOutside, { capture: true });
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		this.setState({
			selectedItems: this.findSelectedItems(nextProps.selectedItems),
		});
	}

	handleAdd = async (item) => {
		this.setState({ cbLoading: true });

		this.inputRef.blur();
		this.hiddenFocusDivRef.focus();

		this.setState({
			openSelection: false,
			filter: "",
			filteredItems: [],
		});
		await this.props.onAdd(item["value"]);
		this.setState({ cbLoading: false });
	};

	// Overrides handleSelect in NuSingleSelect, this function is used when an item is selected via keyboard navigation
	handleSelect = async (item) => {
		this.handleAdd(item);
	};

	handleRemove = (item) => {
		const newSelectedItems = this.state.selectedItems;
		newSelectedItems.splice(newSelectedItems.indexOf(item), 1);
		this.setState({
			selectedItems: newSelectedItems,
		});
		this.props.onRemove(item["value"]);
	};

	prepareFilteredItems = async (filter) => {
		// reset the keyboard focussed item tracking
		this.currentOptionIndex = -1;
		if (this.currentOption) {
			this.currentOption.classList.remove("NuSelect_searchSelectItem--keyboard-focused");
		}
		this.currentOption = null;

		//If there is a callback for allItems, call that now
		let allItems = this.props.allItems;
		if (this.props.allItemsCb) {
			this.setState({ cbLoading: true });
			allItems = await this.props.allItemsCb(filter);
			this.setState({ cbLoading: false });
		}

		const filteredItems = [];
		for (const item of allItems) {
			if (item.dynamic === true) {
				var transformedValue = item.transformFilter(filter);
				if (item.shouldShow(transformedValue)) {
					filteredItems.push({
						text: item.renderText(transformedValue),
						value: transformedValue,
						ok: item.isOk(transformedValue),
					});
				}
			} else {
				if (filter.length <= 2 && item["text"].toLowerCase().startsWith(filter.toLowerCase())) {
					filteredItems.push(item);
				} else if (filter.length > 2 && item["text"].toLowerCase().includes(filter.toLowerCase())) {
					filteredItems.push(item);
				} else if (filter.length === 0) {
					filteredItems.push(item);
				}
			}
		}

		for (const selectedItem of this.state.selectedItems) {
			for (let i = 0; i < filteredItems.length; i++) {
				if (selectedItem["value"] === filteredItems[i]["value"]) {
					filteredItems.splice(i, 1);
					break;
				}
			}
		}
		return filteredItems.slice(0, 100);
	};

	renderItems = () => {
		const preparedItems = [];
		for (const item of this.state.filteredItems) {
			preparedItems.push(
				<div key={item["text"]} className="NuSelect_searchSelectItem" onClick={async () => this.handleAdd(item)}>
					{item["text"]}
				</div>
			);
		}
		if (this.state.filteredItems.length > 99) {
			preparedItems.push(
				<div key="truncated_items_info" className="NuSelect_searchSelectItemTruncateInfo">
					Too many values to show them all. Please use search to narrow down results.
				</div>
			);
		}
		return preparedItems;
	};

	renderSelectedItems = () => {
		const renderItems = [];
		for (const item of this.state.selectedItems) {
			if (item !== undefined && item !== null) {
				renderItems.push(
					<NuSelectItem key={item["value"]} item={item} handleChange={this.handleRemove} showClose={!this.props.disabled} />
				);
			}
		}
		return renderItems;
	};

	render() {
		return (
			<div>
				{this.props.title && <div className="NuSelect_selectedTitle">{this.props.title}</div>}
				{!this.props.disabled && (
					<div
						className={clsx(this.state.openSelection && "NuSelect_searchOpen")}
						onFocus={this.setFocussedClass}
						onBlur={this.removeFocussedClass}
					>
						<div
							ref={this.setSearchContainerRef}
							className={clsx(
								"NuSelect_searchField",
								this.props.invalid && "invalid",
								this.props.loading && "NuSelect_searchField--disabled"
							)}
						>
							<i ref={(node) => (this.searchIconRef = node)} className="fa fa-search NuSelect_searchFieldSearchIcon" />
							<input
								className={clsx("NuSelect_searchFieldInput", !this.state.openSelection && !this.props.disabled && "clickable")}
								ref={this.setInputRef}
								type="text"
								value={this.state.filter}
								onChange={this.handleChangeBounced}
								placeholder={this.props.loading ? "Loading..." : this.props.searchPlaceholder}
								onFocus={this.handleFocus}
								onKeyDown={this.handleInputKeyDown}
								// keyUp used by our Modal class uses keyUp for it's enter handler, we need to stop propagation to that handler so we need to use keyUp here
								onKeyUp={this.handleInputEnterKeyUp}
								data-cy={this.props.dataCyInput}
								disabled={this.props.loading || this.props.disabled}
							/>
							<Box onClick={this.handleClick}>
								<i
									ref={(node) => (this.arrowIconRef = node)}
									className={clsx(
										"fa",
										"NuSelect_searchFieldArrowIcon",
										!this.state.cbLoading && !this.props.allItemsCb && "fa-angle-down",
										this.state.cbLoading && "fa-refresh",
										this.state.cbLoading && "fa-angle-down"
									)}
								/>
							</Box>
						</div>
						{this.renderSelection()}
					</div>
				)}
				{!this.props.loading && (
					<div className="NuSelect_selected" data-cy={this.props.dataCyItemsContainer} data-testid={this.props.dataCyItemsContainer}>
						{this.renderSelectedItems()}
					</div>
				)}
				{/* focus is set here after a selection is made, allows fluent tab navigation back to select, or forward to next tabbable element */}
				<div tabIndex={-1} ref={(node) => (this.hiddenFocusDivRef = node)} className="NuSelect_hiddenFocusDiv" />
			</div>
		);
	}
}

NuMultiSelect.propTypes = {
	dataCyInput: PropTypes.string,
	dataCyItemsContainer: PropTypes.string,
	searchPlaceholder: PropTypes.string,
	selectedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
	allItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
};

NuSingleSelect.defaultProps = {
	allItems: [],
	loading: false,
};
